import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EligibilityWeek } from '../../types';

interface DailyCountCell {
  key: string;
  label: string;
  isWeekend: boolean;
  count: number;
}

// Friday counts as weekend on both PFP rules. See
// agnus-server/preferredPartner/eligibility/carrier.go (DailyCapacityVerdict
// doc + maxWeekend = max(Fri, Sat, Sun)). Keep this map in sync with that
// constant or the highlight in the dialog will silently drift away from how
// the verdict is computed.
const DAY_ORDER: ReadonlyArray<{ key: string; label: string; isWeekend: boolean }> = [
  { key: 'mon', label: 'Mon', isWeekend: false },
  { key: 'tue', label: 'Tue', isWeekend: false },
  { key: 'wed', label: 'Wed', isWeekend: false },
  { key: 'thu', label: 'Thu', isWeekend: false },
  { key: 'fri', label: 'Fri', isWeekend: true },
  { key: 'sat', label: 'Sat', isWeekend: true },
  { key: 'sun', label: 'Sun', isWeekend: true },
];

/**
 * Dialog payload for the Overview page. Surfaces:
 *   - the current verdict (carrierEligible),
 *   - the override badge (if `override !== null`),
 *   - the PRE-override rule breakdowns (effectiveBreakdowns).
 *
 * Whenever an override exists, we make it crystal clear that the surfaced
 * verdict is the manually-overridden one (post-override) while the rule
 * details below describe the evaluator's original opinion (pre-override).
 */
@Component({
  selector: 'lohi-eligibility-detail-dialog',
  templateUrl: './eligibility-detail-dialog.component.html',
  styleUrls: ['./eligibility-detail-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EligibilityDetailDialogComponent {
  public readonly week: EligibilityWeek;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { week: EligibilityWeek },
    private dialogRef: MatDialogRef<EligibilityDetailDialogComponent>,
  ) {
    this.week = data.week;
  }

  public close(): void {
    this.dialogRef.close();
  }

  /**
   * Returns the daily-count grid in fixed Mon→Sun order so the carrier can
   * see at a glance which days drove the weekday/weekend split, instead of
   * having to read the raw JSON keys (which arrive alphabetised).
   */
  public dailyCountCells(counts: Record<string, number> | null | undefined): DailyCountCell[] {
    return DAY_ORDER.map((d) => ({
      ...d,
      count: this.lookupCount(counts, d.key),
    }));
  }

  private lookupCount(counts: Record<string, number> | null | undefined, key: string): number {
    if (!counts) {
      return 0;
    }
    return counts[key] ?? counts[key.toUpperCase()] ?? counts[capitalize(key)] ?? 0;
  }

  public trackByDayKey(index: number, cell: DailyCountCell): string {
    return cell.key;
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
